import type { CellContext } from '@tanstack/react-table';
import type { DataGridColumnDef, DataGridColumnDefOptions } from '../../data-grid.type';
import { Tag } from '@components/atoms';
import type { TAllowedColors } from '@components/atoms/tag/types';

export interface TagItem {
  color?: TAllowedColors;
  value: string;
  secondaryContent?: string;
}

const DEFAULT_COLOR: TAllowedColors = 'green';

/**
 * Generates a cell renderer function for displaying tags in a data grid column.
 *
 * @template T - The type of data being displayed in the grid.
 * @param {DataGridColumnDef<T>} col - The column definition.
 * @param {DataGridColumnDefOptions} options - Additional options for the column.
 * @returns {Function} A function that renders the cell content as tags.
 */
export function getColumnDefTags<T>(col: DataGridColumnDef<T>, options: DataGridColumnDefOptions) {
  return function TagsCell(context: CellContext<T, unknown>) {
    const value = context.getValue();
    const row = context.row.original;

    if (!value || (Array.isArray(value) && value.length === 0)) {
      return null;
    }

    /**
     * Normalizes a partial TagItem object to ensure it has a value and color.
     *
     * @param {Partial<TagItem>} item - The partial TagItem object.
     * @returns {TagItem} The normalized TagItem object.
     */
    const normalizeTagItem = (item: Partial<TagItem>): TagItem => ({
      value: String(item.value ?? ''),
      color: item.color ?? DEFAULT_COLOR,
    });

    const parsedValue = options?.parse ? options.parse(value, row, options) : value;
    const items = Array.isArray(parsedValue) ? parsedValue : [parsedValue];

    const tagItems: TagItem[] = items.map(item => normalizeTagItem(typeof item === 'object' ? item : { value: item }));

    return (
      <div className="flex flex-wrap gap-1">
        {tagItems.map((tag, index) => (
          <Tag key={index} color={tag.color as TAllowedColors}>
            {tag.value}
          </Tag>
        ))}
      </div>
    );
  };
}
